// services/chatDrafts.ts
//
// The two writes a chat-authored draft question accepts (ORA-934; backend
// ORA-937).
//
// THE CONTRACT THIS CLIENT IS WRITTEN AGAINST — ora-be must match it, or bump
// it and tell this file:
//
//   POST /api/v5/chat/drafts/:draftId/generate
//   200 <block payload>            — the row is READY or FAILED; render it
//   202 { draftId, status: 'pending' } — someone else's request is writing it
//   404 — unknown draft, or someone else's
//
//   GET  /api/v5/chat/drafts/:draftId
//   200 <block payload for the row's current status>   — what a 202 is polled on
//
//   POST /api/v5/chat/drafts/:draftId/answer
//   body: { letter: 'A' | 'B' | 'C' | 'D' | 'E' }
//   200 { draftId, selectedLetter, correctLetter }
//   404 — unknown draft, or someone else's
//
//   POST /api/v5/chat/drafts/:draftId/feedback
//   body: { reason: ChatDraftFeedbackReason, note?: string | null }
//   200 { draftId, feedbackReason }
//   404 — unknown draft, or someone else's
//
// Notes on the shape, and why:
//
// - A draft is not scored. The answer write records which option the student
//   picked so a reloaded thread shows the same revealed state and the daily
//   report can read what drafts were answered; it does not register a QBank
//   response, does not spend the free-tier question allowance, and does not
//   feed scheduling. That is the whole reason a draft has endpoints of its own
//   rather than reusing `submitChatVignetteAnswer`.
// - Writing a question takes longer than a turn may last, so the block is
//   served PENDING and the generation happens inside the `generate` request the
//   block itself makes. That request can legitimately run for over a minute:
//   the server budgets 90 s, so the client waits 95 s before giving up on it.
//   A second, concurrent request for the same draft is answered 202 rather than
//   generating twice, which is what the poll is for.
// - The first pick wins and the endpoint is idempotent: a second call answers
//   with the stored letter. The block locks after one pick anyway, so a repeat
//   only happens if the same thread is open twice.
// - Neither call throws. The reveal is already on screen when the request goes
//   out (the correct letter is in the payload, as it is for a vetted question),
//   so a failed write has nothing to roll back and nothing worth an error
//   state; the boolean is there for the caller that wants to know.

import axiosInstance from './axiosCreator';

/**
 * How long the client waits on one `generate` request. The server's own budget
 * for the whole run is 90 s; this sits just outside it, so the request is given
 * up on only when the server itself has stopped answering.
 */
export const CHAT_DRAFT_GENERATE_TIMEOUT_MS = 95_000;

/** How often the block asks again after a 202, and for how long in total. */
export const CHAT_DRAFT_POLL_INTERVAL_MS = 3_000;
export const CHAT_DRAFT_POLL_WINDOW_MS = 120_000;

/**
 * What a read of a draft came back with: the payload to render, "someone is
 * already writing it" (a 202, which the caller polls on), or a failure the
 * caller turns into the failed state. The payload is returned raw because the
 * block parses it with the same parser the registry uses.
 */
export type ChatDraftReadResult =
  | { kind: 'payload'; payload: Record<string, unknown> }
  | { kind: 'in_flight' }
  | { kind: 'error' };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The payload out of a response body. The endpoints return the block payload
 * itself; a body that wraps it in `data` (the shape some v5 reads use) is
 * unwrapped rather than failed, so one endpoint's envelope does not blank a
 * student's question.
 */
const payloadFrom = (body: unknown): Record<string, unknown> | null => {
  if (!isRecord(body)) return null;
  if (typeof body.status === 'string') return body;
  return isRecord(body.data) ? body.data : null;
};

/** The thumbs-down reasons, as the Prisma enum spells them. */
export const CHAT_DRAFT_FEEDBACK_REASONS = [
  'WRONG_ANSWER',
  'TWO_RIGHT_ANSWERS',
  'NOT_FROM_MY_SOURCE',
  'UNCLEAR_STEM',
] as const;

export type ChatDraftFeedbackReason =
  (typeof CHAT_DRAFT_FEEDBACK_REASONS)[number];

export const chatDraftRoute = (draftId: string): string =>
  `/api/v5/chat/drafts/${encodeURIComponent(draftId)}`;

export const chatDraftGenerateRoute = (draftId: string): string =>
  `${chatDraftRoute(draftId)}/generate`;

export const chatDraftAnswerRoute = (draftId: string): string =>
  `/api/v5/chat/drafts/${encodeURIComponent(draftId)}/answer`;

export const chatDraftFeedbackRoute = (draftId: string): string =>
  `/api/v5/chat/drafts/${encodeURIComponent(draftId)}/feedback`;

/** Record the option the student picked. Resolves true when it was stored. */
export const answerChatDraftQuestion = async (
  draftId: string,
  letter: string
): Promise<boolean> => {
  try {
    await axiosInstance.post(chatDraftAnswerRoute(draftId), { letter });
    return true;
  } catch {
    return false;
  }
};

/** Record a thumbs-down on a draft. Resolves true when it was stored. */
export const sendChatDraftQuestionFeedback = async (
  draftId: string,
  reason: ChatDraftFeedbackReason
): Promise<boolean> => {
  try {
    await axiosInstance.post(chatDraftFeedbackRoute(draftId), { reason });
    return true;
  } catch {
    return false;
  }
};

/**
 * The generate requests this tab has open, by draft.
 *
 * One draft can be on screen twice — the same block in the page thread and in
 * the drawer, or two renderers of one conversation — and each of them mounts a
 * block that wants its question written. The server answers the second request
 * `202` and generates once either way, but the second request is still a round
 * trip for a question that is already being written, so callers for a draft
 * that is already in flight await the first request instead of making one.
 *
 * The entry is dropped when the request settles, so a later mount (a reload, a
 * second conversation open) asks again rather than replaying an old answer.
 */
const generateRequests = new Map<string, Promise<ChatDraftReadResult>>();

const requestGeneration = async (
  draftId: string
): Promise<ChatDraftReadResult> => {
  try {
    const response = await axiosInstance.post(
      chatDraftGenerateRoute(draftId),
      {},
      { timeout: CHAT_DRAFT_GENERATE_TIMEOUT_MS }
    );

    if (response.status === 202) return { kind: 'in_flight' };

    const payload = payloadFrom(response.data);
    return payload ? { kind: 'payload', payload } : { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
};

/**
 * Ask the server to write the question for a pending draft. Resolves with the
 * finished payload (ready or failed), `in_flight` when another request is
 * already writing it, or `error` when the request itself failed.
 */
export const generateChatDraftQuestion = (
  draftId: string
): Promise<ChatDraftReadResult> => {
  const open = generateRequests.get(draftId);
  if (open) return open;

  const request = requestGeneration(draftId).finally(() => {
    generateRequests.delete(draftId);
  });

  generateRequests.set(draftId, request);

  return request;
};

/** Read a draft's current state. What the block polls after a 202. */
export const fetchChatDraftQuestion = async (
  draftId: string
): Promise<ChatDraftReadResult> => {
  try {
    const response = await axiosInstance.get(chatDraftRoute(draftId));
    const payload = payloadFrom(response.data);
    return payload ? { kind: 'payload', payload } : { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
};
